import React, { useState, useEffect, useCallback } from 'react';
import {
  Rocket, Plus, X, Key, Settings, Server,
  KeyRound, Lock, HardDrive, AlertTriangle, Save, RotateCcw,
  Globe, Network, Copy, Shield, ShieldOff, Link, Info
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import apiService from '../services/api';
import SSHKeyManager from './SSHKeyManager';

const InstallationForm = ({ onJobCreated, savedConfig, onSaveConfig }) => {
  const [nodes, setNodes] = useState([
    { host: '', privateIp: '', username: '', password: '', privateKey: '', authType: 'password' }
  ]);
  const [version, setVersion] = useState('3.6.1');
  const [installPath, setInstallPath] = useState('/opt/kafka');
  const [dataPath, setDataPath] = useState('/var/lib/kafka');
  const [logPath, setLogPath] = useState('/var/log/kafka');
  const [cleanup, setCleanup] = useState(false);
  const [useKRaft, setUseKRaft] = useState(true);
  const [zkHeap, setZkHeap] = useState('512M');
  const [kafkaHeap, setKafkaHeap] = useState('1G');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [showSSHManager, setShowSSHManager] = useState(false);
  const [useSSL, setUseSSL] = useState(false);
  const [useSharedCredentials, setUseSharedCredentials] = useState(true);
  const [sharedCredentials, setSharedCredentials] = useState({
    username: '',
    password: '',
    privateKey: '',
    authType: 'password'
  });
  // Offline/Custom download URL
  const [useCustomUrl, setUseCustomUrl] = useState(false);
  const [customDownloadUrl, setCustomDownloadUrl] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [downloadBaseUrl, setDownloadBaseUrl] = useState('https://archive.apache.org/dist/kafka');

  const loadFromConfig = useCallback((config) => {
    if (config.nodes) setNodes(config.nodes);
    if (config.version) setVersion(config.version);
    if (config.installPath) setInstallPath(config.installPath);
    if (config.dataPath) setDataPath(config.dataPath);
    if (config.logPath) setLogPath(config.logPath);
    if (config.cleanup !== undefined) setCleanup(config.cleanup);
    if (config.useKRaft !== undefined) setUseKRaft(config.useKRaft);
    if (config.zkHeap) setZkHeap(config.zkHeap);
    if (config.kafkaHeap) setKafkaHeap(config.kafkaHeap);
    if (config.useSharedCredentials !== undefined) setUseSharedCredentials(config.useSharedCredentials);
    if (config.sharedCredentials) setSharedCredentials(config.sharedCredentials);
    if (config.useSSL !== undefined) setUseSSL(config.useSSL);
    if (config.useCustomUrl !== undefined) setUseCustomUrl(config.useCustomUrl);
    if (config.customDownloadUrl) setCustomDownloadUrl(config.customDownloadUrl);
  }, []);

  const loadKafkaVersions = useCallback(async () => {
    try {
      const response = await apiService.getKafkaVersions();
      setAvailableVersions(response.versions || []);
      if (response.downloadBaseUrl) setDownloadBaseUrl(response.downloadBaseUrl);
      if (response.recommended && !savedConfig) {
        setVersion(response.recommended);
      }
    } catch (err) {
      console.error('Failed to load Kafka versions:', err);
    }
  }, [savedConfig]);

  useEffect(() => {
    if (savedConfig) {
      loadFromConfig(savedConfig);
    }
  }, [savedConfig, loadFromConfig]);

  useEffect(() => {
    loadKafkaVersions();
  }, [loadKafkaVersions]);

  const getCurrentConfig = () => ({
    nodes, version, installPath, dataPath, logPath, cleanup, useKRaft,
    zkHeap, kafkaHeap, useSharedCredentials, sharedCredentials, useSSL,
    useCustomUrl, customDownloadUrl
  });

  const handleSaveConfig = () => onSaveConfig(getCurrentConfig());
  const handleLoadConfig = () => { if (savedConfig) loadFromConfig(savedConfig); };

  const addNode = () => {
    const newNode = useSharedCredentials
      ? { host: '', privateIp: '', username: sharedCredentials.username, password: sharedCredentials.password, privateKey: sharedCredentials.privateKey, authType: sharedCredentials.authType }
      : { host: '', privateIp: '', username: '', password: '', privateKey: '', authType: 'password' };
    setNodes([...nodes, newNode]);
  };

  const removeNode = (index) => {
    if (nodes.length > 1) setNodes(nodes.filter((_, i) => i !== index));
  };

  const updateNode = (index, field, value) => {
    const updatedNodes = [...nodes];
    updatedNodes[index][field] = value;
    setNodes(updatedNodes);
  };

  const applySharedCredentialsToAll = () => {
    setNodes(nodes.map(node => ({
      ...node,
      username: sharedCredentials.username,
      password: sharedCredentials.authType === 'password' ? sharedCredentials.password : '',
      privateKey: sharedCredentials.authType === 'key' ? sharedCredentials.privateKey : '',
      authType: sharedCredentials.authType
    })));
  };

  const handleSelectKey = async (key) => {
    try {
      const response = await apiService.getSSHKey(key.id);
      if (response.success) {
        const keyData = response.key;
        setSharedCredentials({
          username: keyData.username,
          password: keyData.password || '',
          privateKey: keyData.privateKey || '',
          authType: keyData.privateKey ? 'key' : 'password'
        });
        setUseSharedCredentials(true);
        setNodes(nodes.map(node => ({
          ...node,
          username: keyData.username,
          password: keyData.password || '',
          privateKey: keyData.privateKey || '',
          authType: keyData.privateKey ? 'key' : 'password'
        })));
        setShowSSHManager(false);
      }
    } catch (err) {
      setError('Failed to load key: ' + err.message);
    }
  };

  // Generate the default download URL based on version
  const getDefaultDownloadUrl = (ver) => {
    return `${downloadBaseUrl}/${ver}/kafka_2.13-${ver}.tgz`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (nodes.length === 2) throw new Error('Cluster requires either 1 node or at least 3 nodes');

      const finalNodes = nodes.map(node => {
        const creds = useSharedCredentials ? sharedCredentials : node;
        return {
          host: node.host, privateIp: node.privateIp || '', username: creds.username,
          password: creds.authType === 'password' ? creds.password : undefined,
          privateKey: creds.authType === 'key' ? creds.privateKey : undefined
        };
      });

      for (const node of finalNodes) {
        if (!node.host || !node.username) throw new Error('All nodes must have host and username');
        if (!node.password && !node.privateKey) throw new Error('All nodes must have either password or private key');
      }

      // Validate custom URL if enabled
      if (useCustomUrl && !customDownloadUrl) {
        throw new Error('Custom download URL is required when offline mode is enabled');
      }

      handleSaveConfig();

      const response = await apiService.createInstallationJob({
        nodes: finalNodes, 
        version, 
        installPath, 
        dataPath, 
        logPath, 
        cleanup, 
        useKRaft,
        security: { ssl: useSSL },
        javaHeapSize: { zookeeper: zkHeap, kafka: kafkaHeap },
        customDownloadUrl: useCustomUrl ? customDownloadUrl : null
      });

      if (response.success) onJobCreated(response.jobId);
      else throw new Error(response.message || 'Failed to create job');
    } catch (err) {
      setError(err.message || 'Failed to submit installation request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Rocket className="h-5 w-5 text-primary" />
          <h2 className="text-base font-bold">Deploy Kafka Cluster</h2>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="outline" size="sm" onClick={handleSaveConfig} className="h-8 text-xs">
                <Save className="h-4 w-4 mr-1" />Save
              </Button>
            </TooltipTrigger>
            <TooltipContent>Save current configuration</TooltipContent>
          </Tooltip>
          {savedConfig && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="outline" size="sm" onClick={handleLoadConfig} className="h-8 text-xs">
                  <RotateCcw className="h-4 w-4 mr-1" />Load
                </Button>
              </TooltipTrigger>
              <TooltipContent>Load saved configuration</TooltipContent>
            </Tooltip>
          )}
          <Dialog open={showSSHManager} onOpenChange={setShowSSHManager}>
            <DialogTrigger asChild>
              <Button type="button" variant="secondary" size="sm" className="h-8 text-xs">
                <Key className="h-4 w-4 mr-1" />SSH Keys
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>SSH Key Manager</DialogTitle></DialogHeader>
              <SSHKeyManager onSelectKey={handleSelectKey} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="py-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs ml-2">{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Settings className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Kafka Mode</span>
          </div>
          <RadioGroup value={useKRaft.toString()} onValueChange={(v) => setUseKRaft(v === 'true')} className="space-y-2">
            <label className="flex items-center gap-2 p-2 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/50">
              <RadioGroupItem value="true" className="h-4 w-4" />
              <div className="flex-1">
                <span className="text-xs font-medium">KRaft</span>
                <Badge variant="success" className="ml-1 text-[9px] h-4 px-1">Recommended</Badge>
                <p className="text-[10px] text-muted-foreground">No ZooKeeper (v3.3+)</p>
              </div>
            </label>
            <label className="flex items-center gap-2 p-2 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/50">
              <RadioGroupItem value="false" className="h-4 w-4" />
              <div className="flex-1">
                <span className="text-xs font-medium">Legacy</span>
                <p className="text-[10px] text-muted-foreground">With ZooKeeper</p>
              </div>
            </label>
          </RadioGroup>
          <Separator className="my-3" />
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Security</span>
          </div>
          <div className="flex items-center justify-between p-2 rounded-lg border border-border/50">
            <div className="flex items-center gap-2">
              {useSSL ? <Shield className="h-4 w-4 text-green-500" /> : <ShieldOff className="h-4 w-4 text-muted-foreground" />}
              <div>
                <span className="text-xs font-medium">{useSSL ? 'SSL Enabled' : 'PLAINTEXT'}</span>
                <p className="text-[10px] text-muted-foreground">{useSSL ? 'Encrypted' : 'No encryption'}</p>
              </div>
            </div>
            <Switch checked={useSSL} onCheckedChange={setUseSSL} />
          </div>
        </Card>

        <Card className="p-4 col-span-3">
          <div className="flex items-center gap-2 mb-3">
            <HardDrive className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Configuration</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Kafka Version</Label>
              <Select value={version} onValueChange={setVersion}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableVersions.map(v => <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Kafka Heap</Label>
              <Input value={kafkaHeap} onChange={(e) => setKafkaHeap(e.target.value)} className="h-8 text-xs" placeholder="1G" />
            </div>
            {!useKRaft && (
              <div className="space-y-1.5">
                <Label className="text-xs">ZK Heap</Label>
                <Input value={zkHeap} onChange={(e) => setZkHeap(e.target.value)} className="h-8 text-xs" placeholder="512M" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Install Path</Label>
              <Input value={installPath} onChange={(e) => setInstallPath(e.target.value)} className="h-8 text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Data Path</Label>
              <Input value={dataPath} onChange={(e) => setDataPath(e.target.value)} className="h-8 text-xs" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Log Path</Label>
              <Input value={logPath} onChange={(e) => setLogPath(e.target.value)} className="h-8 text-xs" />
            </div>
          </div>
          <Separator className="my-3" />
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-medium">Cleanup existing installation</span>
              <p className="text-[10px] text-muted-foreground">Remove old Kafka before installing</p>
            </div>
            <Switch checked={cleanup} onCheckedChange={setCleanup} />
          </div>
          
          {/* Advanced / Offline Installation Options */}
          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced} className="mt-3">
            <CollapsibleTrigger className="flex items-center gap-2 text-xs text-primary hover:underline cursor-pointer">
              <Link className="h-3 w-3" />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Download Options
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-3 border border-border/50 rounded-lg bg-muted/30">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Link className="h-4 w-4 text-primary" />
                  <span className="text-xs font-medium">Custom Download URL</span>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="h-3 w-3 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="text-xs">Use a custom URL to download Kafka. Useful for:</p>
                      <ul className="text-xs mt-1 list-disc list-inside">
                        <li>Offline/air-gapped installations</li>
                        <li>Internal mirrors or artifact repositories</li>
                        <li>Custom Kafka builds</li>
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Switch checked={useCustomUrl} onCheckedChange={setUseCustomUrl} />
              </div>
              {useCustomUrl ? (
                <div className="space-y-2">
                  <Input 
                    value={customDownloadUrl} 
                    onChange={(e) => setCustomDownloadUrl(e.target.value)} 
                    className="h-8 text-xs font-mono" 
                    placeholder="https://your-mirror.com/kafka_2.13-3.6.1.tgz"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Enter the full URL to the Kafka .tgz file. The file should be a standard Apache Kafka distribution.
                  </p>
                </div>
              ) : (
                <p className="text-[10px] text-muted-foreground">
                  Default URL: <code className="bg-muted px-1 rounded">{getDefaultDownloadUrl(version)}</code>
                </p>
              )}
            </CollapsibleContent>
          </Collapsible>
        </Card>
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">SSH Credentials</span>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="useShared" checked={useSharedCredentials} onCheckedChange={setUseSharedCredentials} />
            <Label htmlFor="useShared" className="text-xs cursor-pointer">Use same credentials for all servers</Label>
          </div>
        </div>
        {useSharedCredentials && (
          <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 mb-3">
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Username</Label>
                <Input value={sharedCredentials.username} onChange={(e) => setSharedCredentials({ ...sharedCredentials, username: e.target.value })} placeholder="ec2-user" className="h-8 text-xs" />
              </div>
              <div className="col-span-2">
                <Tabs value={sharedCredentials.authType} onValueChange={(v) => setSharedCredentials({ ...sharedCredentials, authType: v })}>
                  <TabsList className="h-7 w-full grid grid-cols-2 bg-muted/50 mb-2">
                    <TabsTrigger value="password" className="text-xs h-6"><Lock className="h-3 w-3 mr-1" />Password</TabsTrigger>
                    <TabsTrigger value="key" className="text-xs h-6"><KeyRound className="h-3 w-3 mr-1" />SSH Key</TabsTrigger>
                  </TabsList>
                  <TabsContent value="password" className="mt-0">
                    <Input type="password" value={sharedCredentials.password} onChange={(e) => setSharedCredentials({ ...sharedCredentials, password: e.target.value })} placeholder="SSH Password" className="h-8 text-xs" />
                  </TabsContent>
                  <TabsContent value="key" className="mt-0">
                    <Textarea value={sharedCredentials.privateKey} onChange={(e) => setSharedCredentials({ ...sharedCredentials, privateKey: e.target.value })} placeholder="-----BEGIN RSA PRIVATE KEY-----" className="h-20 text-xs font-mono resize-none" />
                  </TabsContent>
                </Tabs>
              </div>
              <div className="flex items-end">
                <Button type="button" variant="outline" size="sm" onClick={applySharedCredentialsToAll} className="h-8 text-xs w-full">
                  <Copy className="h-3 w-3 mr-1" />Apply to All
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Server className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Target Servers</span>
            <Badge variant="outline" className="text-xs h-5 px-1.5">{nodes.length} node{nodes.length > 1 ? 's' : ''}</Badge>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addNode} className="h-8 text-xs">
            <Plus className="h-4 w-4 mr-1" />Add Server
          </Button>
        </div>
        {nodes.length === 2 && (
          <Alert variant="warning" className="py-2 mb-3">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs ml-2">2-node cluster is not recommended. Use 1 node or 3+ nodes.</AlertDescription>
          </Alert>
        )}
        <div className="grid grid-cols-3 gap-3 max-h-[350px] overflow-y-auto pr-1">
          {nodes.map((node, index) => (
            <div key={index} className="p-3 border border-border/50 rounded-lg space-y-2 bg-muted/30">
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="text-xs h-5">Node {index + 1}</Badge>
                {nodes.length > 1 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeNode(index)} className="h-6 w-6 p-0 hover:bg-destructive/20">
                    <X className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground flex items-center gap-1"><Globe className="h-3 w-3" />Public IP / Host</Label>
                <Input value={node.host} onChange={(e) => updateNode(index, 'host', e.target.value)} placeholder="54.xx.xx.xx" className="h-8 text-xs" required />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground flex items-center gap-1"><Network className="h-3 w-3" />Private IP (Optional)</Label>
                <Input value={node.privateIp} onChange={(e) => updateNode(index, 'privateIp', e.target.value)} placeholder="10.0.x.x" className="h-8 text-xs" />
              </div>
              {!useSharedCredentials && (
                <>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-muted-foreground">Username</Label>
                    <Input value={node.username} onChange={(e) => updateNode(index, 'username', e.target.value)} placeholder="ec2-user" className="h-8 text-xs" required />
                  </div>
                  <Tabs value={node.authType} onValueChange={(v) => updateNode(index, 'authType', v)}>
                    <TabsList className="h-6 w-full grid grid-cols-2 bg-muted/50">
                      <TabsTrigger value="password" className="text-[10px] h-5"><Lock className="h-2.5 w-2.5 mr-0.5" />Password</TabsTrigger>
                      <TabsTrigger value="key" className="text-[10px] h-5"><KeyRound className="h-2.5 w-2.5 mr-0.5" />SSH Key</TabsTrigger>
                    </TabsList>
                    <TabsContent value="password" className="mt-1">
                      <Input type="password" value={node.password} onChange={(e) => updateNode(index, 'password', e.target.value)} placeholder="Password" className="h-7 text-xs" />
                    </TabsContent>
                    <TabsContent value="key" className="mt-1">
                      <Textarea value={node.privateKey} onChange={(e) => updateNode(index, 'privateKey', e.target.value)} placeholder="SSH Key" className="h-16 text-[10px] font-mono resize-none" />
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="mt-3 p-2 rounded bg-muted/50 text-xs text-muted-foreground">
          <strong>AWS Users:</strong> Public IP for SSH, Private IP for cluster communication. Leave Private IP empty to auto-detect.
        </div>
      </Card>

      <Button type="submit" className="w-full h-10 text-sm font-semibold glow-primary" disabled={loading || nodes.length === 2}>
        {loading ? (
          <><span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />Creating Job...</>
        ) : (
          <><Rocket className="h-4 w-4 mr-2" />Deploy Kafka Cluster ({useKRaft ? 'KRaft' : 'Legacy'} / {useSSL ? 'SSL' : 'PLAINTEXT'}{useCustomUrl ? ' / Custom URL' : ''})</>
        )}
      </Button>
    </form>
  );
};

export default InstallationForm;
